import { defineStore } from 'pinia';
import http from '@/services/http';

export const useQuestionGroupsStore = defineStore('questionGroups', {
  state: () => ({
    questionGroups: [],
    loading: false,
    error: null,
    currentQuestionGroup: null
  }),

  getters: {
    getAllQuestionGroups: (state) => state.questionGroups,
    getActiveQuestionGroups: (state) => state.questionGroups.filter(group => group.is_active),
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getCurrentQuestionGroup: (state) => state.currentQuestionGroup
  },

  actions: {
    async fetchQuestionGroups() {
      this.loading = true;
      this.error = null;
      this.questionGroups = [];
      
      try {
        const response = await http.get('/api/question-groups');
        const data = Array.isArray(response.data) ? response.data : [];
        // Deduplizieren nach ID (Sicherheitsnetz)
        const seen = new Set();
        this.questionGroups = data.filter(g => {
          if (!g || g.id == null) return false;
          if (seen.has(g.id)) return false;
          seen.add(g.id);
          return true;
        });
      } catch (error) {
        // Bei 401/403 Fehlern (nicht angemeldet/keine Berechtigung) einfach leeres Array setzen
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.warn('Keine Berechtigung für Fragengruppen oder nicht angemeldet');
          this.questionGroups = [];
          return;
        }
        
        this.error = error.response?.data?.message || 'Fehler beim Laden der Fragengruppen';
        console.error('Error fetching question groups:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async createQuestionGroup(questionGroupData) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await http.post('/api/question-groups', questionGroupData);
        await this.fetchQuestionGroups();
        return response.data;
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.error = 'Keine Berechtigung zum Erstellen von Fragengruppen';
        } else {
          this.error = error.response?.data?.message || 'Fehler beim Erstellen der Fragengruppe';
        }
        console.error('Error creating question group:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateQuestionGroup(questionGroupId, questionGroupData) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await http.put(`/api/question-groups/${questionGroupId}`, questionGroupData);
        await this.fetchQuestionGroups();
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Aktualisieren der Fragengruppe';
        console.error('Error updating question group:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteQuestionGroup(questionGroupId) {
      this.loading = true;
      this.error = null;
      
      try {
        await http.delete(`/api/question-groups/${questionGroupId}`);
        await this.fetchQuestionGroups();
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Löschen der Fragengruppe';
        console.error('Error deleting question group:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchQuestionsByGroup(questionGroupId) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await http.get(`/api/question-groups/${questionGroupId}/questions`);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Laden der Fragen der Gruppe';
        console.error('Error fetching questions by group:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async createGroupQuestion(questionGroupId, payload) {
      this.loading = true;
      this.error = null;
      try {
        const response = await http.post(`/api/question-groups/${questionGroupId}/questions`, payload);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Erstellen der Frage';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateGroupQuestion(questionGroupId, questionId, payload) {
      this.loading = true;
      this.error = null;
      try {
        const response = await http.put(`/api/question-groups/${questionGroupId}/questions/${questionId}`, payload);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Aktualisieren der Frage';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteGroupQuestion(questionGroupId, questionId) {
      this.loading = true;
      this.error = null;
      try {
        await http.delete(`/api/question-groups/${questionGroupId}/questions/${questionId}`);
        return true;
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Löschen der Frage';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    setCurrentQuestionGroup(questionGroup) {
      this.currentQuestionGroup = questionGroup;
    },

    clearError() {
      this.error = null;
    }
  }
}); 